/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ComboBox } from 'carbon-components-react';
import _ from 'lodash';
import { string, func, array, bool } from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { MAX_CHAR } from '../../../util/constants';
import { messages } from './messages';

import './ConfigFields.scss';

/**
 * A Component for rending search fields - title, body and URL
 */
export const ConfigFields = ({
  items = [],
  titleOptionValue,
  bodyOptionValue,
  urlOptionValue,
  setTitleOptionValue,
  setBodyOptionValue,
  setUrlOptionValue,
  loadingFields,
  readOnly
}) => {
  const { formatMessage: t } = useIntl();

  const [shouldFilterTitle, setShouldFilterTitle] = useState(true);
  const [shouldFilterBody, setShouldFilterBody] = useState(true);
  const [shouldFilterUrl, setShouldFilterUrl] = useState(true);

  const titleOptionItem = items.find(i => i.id === titleOptionValue);
  const bodyOptionItem = items.find(i => i.id === bodyOptionValue);
  const urlOptionItem = items.find(i => i.id === urlOptionValue);

  // Below is to set selected title, body, url with the Example as Carbon ComboBox does not allow
  // to show secondary text with the selected item (label, 4spaces, separator-line, 4 spaces, example )
  const titleSelectedItem =
    titleOptionValue && titleOptionItem
      ? `${titleOptionValue}    |    ${titleOptionItem.example}`
      : titleOptionValue;
  const bodySelectedItem =
    bodyOptionValue && bodyOptionItem
      ? `${bodyOptionValue}    |    ${bodyOptionItem.example}`
      : bodyOptionValue;
  const urlSelectedItem =
    urlOptionValue && urlOptionItem
      ? `${urlOptionValue}    |    ${urlOptionItem.example}`
      : urlOptionValue;

  const recordAnalytics = option => {
    ctaEvents[`searchFields${option}`]();
  };

  const fields = [
    {
      id: 'SearchDetailConfig-title-dropdown',
      titleText: t(messages.fieldTitle),
      selectedItem: titleSelectedItem,
      shouldFilter: shouldFilterTitle,
      optionValue: titleOptionValue,
      setOptionValue: setTitleOptionValue,
      type: 'Title',
      onInputChange: () => setShouldFilterTitle(true),
      onToggleClick: () => setShouldFilterTitle(false)
    },
    {
      id: 'SearchDetailConfig-body-dropdown',
      titleText: t(messages.fieldBody),
      selectedItem: bodySelectedItem,
      shouldFilter: shouldFilterBody,
      optionValue: bodyOptionValue,
      setOptionValue: setBodyOptionValue,
      type: 'Body',
      onInputChange: () => setShouldFilterBody(true),
      onToggleClick: () => setShouldFilterBody(false)
    },
    {
      id: 'SearchDetailConfig-url-dropdown',
      titleText: t(messages.fieldUrl),
      selectedItem: urlSelectedItem,
      shouldFilter: shouldFilterUrl,
      optionValue: urlOptionValue,
      setOptionValue: setUrlOptionValue,
      type: 'Url',
      onInputChange: () => setShouldFilterUrl(true),
      onToggleClick: () => setShouldFilterUrl(false)
    }
  ];

  return (
    <>
      <p className="ConfigFields-instruction">
        {t(messages.configInstruction)}
      </p>
      {fields.map(
        ({
          id,
          titleText,
          selectedItem,
          shouldFilter,
          optionValue,
          setOptionValue,
          type,
          onInputChange,
          onToggleClick
        }) => (
          <ComboBox
            key={id}
            id={id}
            className={'ConfigFields'}
            titleText={titleText}
            disabled={readOnly}
            items={items}
            placeholder={
              loadingFields
                ? t(messages.loading)
                : t(messages.fieldOptionPlaceholder)
            }
            itemToElement={item => item?.element}
            shouldFilterItem={({ inputValue, item }) =>
              shouldFilter
                ? item.id.toLowerCase().includes(inputValue.toLowerCase())
                : item.id !== optionValue
            }
            onInputChange={onInputChange}
            onToggleClick={onToggleClick}
            onChange={selectedItem => {
              setOptionValue(_.get(selectedItem.selectedItem, 'id', ''));
              recordAnalytics(type);
            }}
            selectedItem={
              selectedItem.length > MAX_CHAR
                ? selectedItem.slice(0, MAX_CHAR).trim() + '...'
                : selectedItem
            }
          />
        )
      )}
    </>
  );
};

ConfigFields.propTypes = {
  items: array,
  titleOptionValue: string,
  bodyOptionValue: string,
  urlOptionValue: string,
  setTitleOptionValue: func,
  setBodyOptionValue: func,
  setUrlOptionValue: func,
  loadingFields: bool,
  readOnly: bool
};
